import {
  useCreateFeePeriodMutation,
  useAccountQuery,
  useFeePeriodLinkedRawTransactionsQuery,
  FeePeriodsByAccountDocument,
} from '@transactions/graphql';
import {parseDate, today} from 'common/date';
import {Pencil, Receipt} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import {CardProductSelect} from 'src/CardProductSelect';
import DatePicker from 'src/DatePicker/DatePicker';
import {RawTransactionPicker} from 'src/inputs/RawTransactionPicker';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import type {DateTime} from 'luxon';
import type {RawTransactionSelection} from 'src/inputs/RawTransactionPicker';
import type {SystemProps} from 'src/Modals';

export type CreateFeePeriodModalUserProps = {
  accountId: string;
  excludeRawTransactionIds?: string[];
  onSuccess: () => void;
};

const formatAmount = (cents: number): string => {
  const abs = Math.abs(cents) / 100;
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${abs.toFixed(2)}`;
};

export const CreateFeePeriodModal = ({
  onRequestClose,
  accountId,
  excludeRawTransactionIds,
  onSuccess,
}: CreateFeePeriodModalUserProps & SystemProps) => {
  const formId = React.useId();
  const [selectedTransaction, setSelectedTransaction] =
    React.useState<RawTransactionSelection | null>(null);
  const [showPicker, setShowPicker] = React.useState(false);
  const initialStart = today();
  const [startDate, setStartDate] = React.useState<DateTime>(initialStart);
  const [endDate, setEndDate] = React.useState<DateTime>(
    initialStart.plus({years: 1}).minus({days: 1}),
  );
  const [payee, setPayee] = React.useState('');
  const [cardProduct, setCardProduct] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  // Prefill the payee (card product name) with the account name as a sensible
  // default. Only fill the field once, and never clobber anything the user has
  // already typed while the account loads.
  const {data: accountData} = useAccountQuery({variables: {id: accountId}});
  const accountName = accountData?.account?.name;
  const prefilledPayee = React.useRef(false);
  React.useEffect(() => {
    if (!prefilledPayee.current && accountName) {
      prefilledPayee.current = true;
      setPayee((prev) => (prev === '' ? accountName : prev));
    }
  }, [accountName]);

  // Likewise the face: a new fee year is almost always the card's current
  // product, so start there and let the user pick otherwise the year the
  // card changed identity.
  const accountCardProduct =
    accountData?.account?.__typename === 'DepositoryAccount'
      ? accountData.account.cardProduct
      : null;
  const prefilledCardProduct = React.useRef(false);
  React.useEffect(() => {
    if (!prefilledCardProduct.current && accountCardProduct) {
      prefilledCardProduct.current = true;
      setCardProduct((prev) => (prev === '' ? accountCardProduct : prev));
    }
  }, [accountCardProduct]);

  // Guard against linking a charge that already backs a fee period — surface
  // it here (and block Create) rather than only failing server-side after the
  // user has filled out the form. Query all fee periods, not just this
  // account's: a fee period keeps its rawTransactionId even after the charge
  // is moved to another account. Read past the cache so a period created on
  // another device/tab isn't missed, and hold Create until it resolves.
  const {data: linkedData, loading: linkedLoading} =
    useFeePeriodLinkedRawTransactionsQuery({fetchPolicy: 'network-only'});
  const linkedRawTransactionIds = React.useMemo(
    () =>
      new Set((linkedData?.allFeePeriods ?? []).map((fp) => fp.rawTransactionId)),
    [linkedData],
  );
  const alreadyLinked =
    selectedTransaction != null &&
    linkedRawTransactionIds.has(selectedTransaction._id);
  // Until the fresh list has resolved we can't be sure the selected charge is
  // free, so keep Create disabled while the check is pending.
  const linkCheckPending = selectedTransaction != null && linkedLoading;

  const [createFeePeriod, {loading}] = useCreateFeePeriodMutation();

  const handleTransactionSelected = (
    _id: string,
    tx?: RawTransactionSelection,
  ) => {
    setSelectedTransaction(tx ?? null);
    setShowPicker(false);
    if (tx) {
      // Auto-fill start date from the transaction date
      const start = parseDate(tx.date);
      if (start.isValid) {
        setStartDate(start);
        // Default end date to one year minus one day from the transaction date
        setEndDate(start.plus({years: 1}).minus({days: 1}));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (!selectedTransaction)
        throw new Error('Fee transaction is required');

      if (!startDate.isValid) throw new Error('Invalid start date');
      if (!endDate.isValid) throw new Error('Invalid end date');

      await createFeePeriod({
        variables: {
          accountId,
          rawTransactionId: selectedTransaction._id,
          startDate: Math.floor(startDate.toSeconds()),
          // The picker's end date is inclusive; stored bounds are half-open
          endDate: Math.floor(endDate.plus({days: 1}).toSeconds()),
          amount: Math.abs(selectedTransaction.amount),
          payee: payee || undefined,
          cardProduct: cardProduct || undefined,
        },
        refetchQueries: [
          {query: FeePeriodsByAccountDocument, variables: {accountId}},
        ],
      });
      onSuccess();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={
          loading || !selectedTransaction || alreadyLinked || linkCheckPending
        }
      >
        {loading ? 'Creating...' : 'Create'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Create Fee Period"
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        {/* The transaction picker contains its own search <form>, so it must
            live outside the create <form> below to avoid nested forms (which
            make the soft-keyboard search action submit the wrong form). */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Fee Transaction</label>
          {showPicker ? (
            <RawTransactionPicker
              accountId={accountId}
              value={selectedTransaction?._id ?? ''}
              onChange={handleTransactionSelected}
              excludeIds={excludeRawTransactionIds}
            />
          ) : selectedTransaction ? (
            <button
              type="button"
              onClick={() => setShowPicker(true)}
              className="border-border hover:bg-muted/50 flex items-center gap-3 rounded-md border p-3 text-left transition-colors"
            >
              <Receipt className="text-muted-foreground h-4 w-4 shrink-0" />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium">
                  {formatAmount(selectedTransaction.amount)}
                </span>
                <span className="text-muted-foreground text-xs">
                  {selectedTransaction.date}
                </span>
              </div>
              <Pencil className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowPicker(true)}
              className="border-border hover:bg-muted/50 text-muted-foreground flex items-center justify-center gap-2 rounded-md border border-dashed p-4 text-sm transition-colors"
            >
              <Receipt className="h-4 w-4" />
              Select fee transaction
            </button>
          )}
          {alreadyLinked && (
            <p className="text-destructive text-xs">
              This transaction already has a fee period.
            </p>
          )}
        </div>

        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Card that year</label>
            <CardProductSelect
              value={cardProduct}
              onChange={setCardProduct}
              label={null}
              ariaLabel="Card that year"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">
              Payee (Card Product Name)
            </label>
            <TextInput
              value={payee}
              onChange={setPayee}
              placeholder="e.g. Chase Sapphire Reserve"
            />
          </div>

          <div className="flex gap-2">
            <div className="flex flex-1 flex-col gap-1">
              <label className="text-sm font-medium">Start Date</label>
              <DatePicker selectedDate={startDate} onChange={setStartDate} />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <label className="text-sm font-medium">End Date</label>
              <DatePicker selectedDate={endDate} onChange={setEndDate} />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};
